/**
 * Event Detail Page - Shows all items from an event
 * @file src/web/features/events/pages/[id].tsx
 */

import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@voilajsx/uikit/button';
import { useEvent } from '../hooks/useEvents';
import { SEO } from '../../../shared/components';
import {
  Calendar,
  MapPin,
  ArrowLeft,
  Image,
  Video,
  Play,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const EventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const eventId = parseInt(id || '0');
  const { event, loading, error } = useEvent(eventId);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const formattedDate = event?.eventDate
    ? new Date(event.eventDate).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null;

  const openLightbox = (index: number) => {
    setSelectedIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedIndex(null);
    document.body.style.overflow = 'auto';
  };

  const navigateLightbox = (direction: 'prev' | 'next') => {
    if (selectedIndex === null || !event?.items) return;

    const newIndex = direction === 'next'
      ? (selectedIndex + 1) % event.items.length
      : (selectedIndex - 1 + event.items.length) % event.items.length;

    setSelectedIndex(newIndex);
  };

  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedIndex === null) return;

      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
      if (e.key === 'ArrowRight') navigateLightbox('next');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="animate-pulse">
          <div className="bg-gray-200 h-64 rounded-xl mb-6"></div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-gray-200 aspect-square rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="text-center py-12">
          <Calendar className="mx-auto h-16 w-16 text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Event not found</h2>
          <p className="text-gray-500 mb-6">The event you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/events')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Events
          </Button>
        </div>
      </div>
    );
  }

  const selectedItem = selectedIndex !== null ? event.items?.[selectedIndex] : null;

  return (
    <>
      <SEO
        title={`${event.title} - Events - Tuition Point`}
        description={event.description || `View photos and videos from ${event.title}`}
      />
      <div className="container mx-auto px-4 max-w-7xl py-8">
        {/* Back button */}
        <Link
          to="/events"
          className="inline-flex items-center text-gray-600 hover:text-cyan-600 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Link>

        {/* Event Header */}
        <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 rounded-xl p-8 shadow-lg mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {event.title}
          </h1>

          {event.description && (
            <p className="text-cyan-100 text-lg mb-6 max-w-3xl">
              {event.description}
            </p>
          )}

          <div className="flex flex-wrap gap-6 text-white/90">
            {formattedDate && (
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{formattedDate}</span>
              </div>
            )}
            {event.location && (
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                <span>{event.location}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              <span>{event.items?.length || 0} photos/videos</span>
            </div>
          </div>
        </div>

        {/* Gallery Grid */}
        {event.items && event.items.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {event.items.map((item, index) => (
              <div
                key={item.id}
                onClick={() => openLightbox(index)}
                className="relative aspect-square overflow-hidden rounded-lg cursor-pointer group bg-gray-100"
              >
                {item.fileType === 'image' ? (
                  <img
                    src={item.url}
                    alt={item.caption || event.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                ) : (
                  <div className="relative w-full h-full bg-gray-900">
                    <video
                      src={item.url}
                      className="w-full h-full object-cover"
                      muted
                      preload="metadata"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="p-4 bg-white/20 rounded-full backdrop-blur-sm">
                        <Play className="w-8 h-8 text-white fill-white" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="p-2 bg-white/20 rounded-full backdrop-blur-sm">
                    {item.fileType === 'image' ? (
                      <Image className="w-6 h-6 text-white" />
                    ) : (
                      <Play className="w-6 h-6 text-white fill-white" />
                    )}
                  </div>
                </div>

                {/* Caption */}
                {item.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-sm line-clamp-2">{item.caption}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <Image className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No photos yet</h3>
            <p className="text-gray-500">Photos and videos will appear here once uploaded.</p>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
          {/* Close Button */}
          <Button
            onClick={closeLightbox}
            size="sm"
            variant="outline"
            className="absolute top-4 right-4 z-10 bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </Button>

          {/* Navigation Buttons */}
          {event.items && event.items.length > 1 && (
            <>
              <Button
                onClick={() => navigateLightbox('prev')}
                size="sm"
                variant="outline"
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                onClick={() => navigateLightbox('next')}
                size="sm"
                variant="outline"
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}

          {/* Content */}
          <div className="w-full h-full flex flex-col p-4">
            <div className="flex-1 flex items-center justify-center">
              {selectedItem.fileType === 'image' ? (
                <img
                  src={selectedItem.url}
                  alt={selectedItem.caption || event.title}
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <video
                  src={selectedItem.url}
                  controls
                  autoPlay
                  className="max-w-full max-h-full"
                />
              )}
            </div>

            {/* Info bar */}
            <div className="bg-black/50 backdrop-blur-sm p-4 rounded-lg mt-4 text-center">
              {selectedItem.caption && (
                <p className="text-white font-medium mb-2">{selectedItem.caption}</p>
              )}
              <div className="flex items-center justify-center gap-4 text-sm text-white/60">
                <span className="flex items-center gap-1">
                  {selectedItem.fileType === 'image' ? (
                    <Image className="h-4 w-4" />
                  ) : (
                    <Video className="h-4 w-4" />
                  )}
                  {selectedItem.fileType.toUpperCase()}
                </span>
                <span>
                  {selectedIndex !== null && event.items ? `${selectedIndex + 1} of ${event.items.length}` : ''}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EventDetailPage;
